import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import GameService from "../../../service/gameService";
import { CONFIG } from "../../../helpers/constants";
import debounce from "lodash.debounce";
import { useTranslation } from "react-i18next";
import GameCard from "./GameCard";
import { SectionLoader } from "../../../components/ui/loaders/SectionLoader";
import { cn } from "../../../helpers/utils";

const GamesCategoryCard = ({ data, limit = null, selectedProviderFilter, setSelectedProviderFilter, searchGames }) => {
  const location = useLocation();
  const { t } = useTranslation();

  const gameService = new GameService(CONFIG.BASE_URL);

  const [games, setGames] = useState([]);
  const [topGames, setTopGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [offset, setOffset] = useState(0);
  const [totalGames, setTotalGames] = useState(0);
  const [loadingFailed, setLoadingFailed] = useState(false);

  const ppGames = "Pragmatic Play";

  const isLiveCasino = location.pathname.startsWith("/livecasino");

  useEffect(() => {
    const loadGamesByIds = async () => {
      setLoading(true);
      try {
        const response = await gameService.fetchGamesByIds(data.topGames);
        setTopGames(response);
      } catch (error) {
        console.error("Failed to load games:", error);
      } finally {
        setLoading(false);
      }
    };
    loadGamesByIds();
  }, [offset, limit, location]);

  const loadGames = async () => {
    setLoading(true);
    setLoadingFailed(false);

    const isTopGamesOrSlots = ["/casino/slots", "/casino/top-games"].includes(data.path);
    const liveCasinoTypes = ["live-casino-table", "livecasino"];
    // const nonLiveCasinoTypes = ["video-slots", "cpy-games"];
    const filters = {
      type: isLiveCasino ? liveCasinoTypes : data.type,
      ...(data?.category && !selectedProviderFilter ? { category: data.category } : null),
      // ...(selectedProviderFilter &&
      //   selectedProviderFilter?.provider_name !== ppGames && {
      //     provider_name: selectedProviderFilter.provider_name,
      //   }),
      ...(!data.provider && selectedProviderFilter
        ? {
            provider_name: selectedProviderFilter.provider_name,
          }
        : data.provider
        ? { provider_name: data.provider }
        : null),
      ...(data.new ? { new: true } : null),
    };

    try {
      let response;
      let topPragmaticGames = [];

      // ✅ Always fetch main games
      if (data.type === "cpygames") {
        response = await gameService.cpyAllGames();
      } else if (selectedProviderFilter && selectedProviderFilter.provider_name === ppGames) {
        response = await gameService.cpyAllGames();
      } else {
        response = await gameService.fetchGames(offset, limit || 32, filters);
      }

      // ✅ For /slots or /top-games — fetch Pragmatic Play games separately
      if (isTopGamesOrSlots) {
        const cpyResponse = await gameService.cpyAllGames();
        topPragmaticGames = cpyResponse.data.slice(0, 7);
      }

      // ✅ Merge: show Pragmatic Play games first, then others (no duplicates)
      let combinedGames = response?.data || [];
      if (isTopGamesOrSlots && topPragmaticGames.length) {
        const nonDuplicateGames = combinedGames.filter((g) => !topPragmaticGames.some((t) => t._id === g._id || t.gameId === g.gameId));
        combinedGames = [...topPragmaticGames, ...nonDuplicateGames];
      }

      if (combinedGames.length && offset === 0) {
        setGames(combinedGames);
      } else if (combinedGames.length) {
        setGames((prevGames) => {
          const newGames = combinedGames.filter((newGame) => !prevGames.some((prevGame) => prevGame._id === newGame._id));
          return [...prevGames, ...newGames];
        });
      } else {
        setGames([]);
        setLoadingFailed(!isInitialLoad);
      }

      setTotalGames(response.pagination?.total || combinedGames.length);
      setIsInitialLoad(false);
    } catch (error) {
      console.error("Failed to load games:", error);
      setLoadingFailed(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadGames();
  }, [offset, limit, location, selectedProviderFilter]);

  useEffect(() => {
    setLoading(true);
    setTopGames([]);
    const fetchGamesDebounced = debounce(async (term) => {
      try {
        if (term.length >= 3) {
          const response = await gameService.fetchAllGamesByName(term, 1, 20);
          setGames(response.data);
          setTotalGames(response.total || 0);
        } else {
          loadGames();
        }
      } catch (error) {
        console.error("Failed to load games:", error);
        setLoadingFailed(true);
      } finally {
        setLoading(false);
      }
    }, 400);

    fetchGamesDebounced(searchGames);

    return () => fetchGamesDebounced.cancel();
  }, [searchGames]);

  useEffect(() => {
    setGames([]);
    setOffset(0);
    setTotalGames(0);
    setSelectedProviderFilter("");
    setIsInitialLoad(true);
  }, [location]);

  const handleLoadMore = (e) => {
    e.preventDefault();
    setOffset((prevOffset) => prevOffset + (limit || 16));
  };

  const gameType = data.path.split("/").join("");
  const displayedGames = searchGames.length >= 3 && limit ? games?.slice(0, limit) : games;

  console.log(topGames, "topGames in GamesCategoryCard");
  return (
    <>
      <style jsx="true">
        {`
          .game-card .overlay {
            visiblity: none;
          }
          .game-card:hover .overlay,
          .game-card:active .overlay {
            visiblity: visible;
            opacity: 1;
          }
        `}
      </style>
      <div className="md:rounded-lg lg:p-4 p-3 md:mb-4 mb-0">
        {loading && offset === 0 ? (
          <SectionLoader bgColor="dark" />
        ) : displayedGames && displayedGames.length > 0 ? (
          <>
            <div
              className={cn("grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-6 gap-4", isLiveCasino && "grid-cols-1 px-6")}
            >
              {data?.topGames?.length > 0 &&
                topGames.map((game) => {
                  return <GameCard game={game} type={gameType} key={game._id || game.gameId} />;
                })}
              {displayedGames.map((game) => {
                return <GameCard game={game} key={game._id || game.gameId} />;
              })}
            </div>
            {!limit && games.length < totalGames && (
              <div className="text-center mt-8 mb-6">
                <button
                  disabled={loading}
                  onClick={handleLoadMore}
                  className="bg-primary duration-300 cursor-pointer md:text-lg rtl:space-x-reverse font-semibold p-1.5 px-4 text-white py-2 rounded-xl shadow hover:bg-[#494949] transition min-w-[200px]"
                >
                  {loading ? (
                    <div className="flex items-center justify-center rounded">
                      <div className="w-7 h-7 border-4 border-primary-400 border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  ) : (
                    t("Load More Games")
                  )}
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="flex items-center justify-center h-[calc(100dvh-400px)] mb-4 rounded">
            <div className="text-white">
              {loadingFailed ? (
                t("No games available at the moment.")
              ) : (
                <SectionLoader bgColor="dark" />
                // <div className="flex items-center justify-center bg-[#2E2E2E] mb-4 rounded">
                //   <div className="w-16 h-16 border-4 border-primary-400 border-t-transparent rounded-full animate-spin"></div>
                // </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default GamesCategoryCard;
